/**
 * Whether to put the install and notifications screen up on this launch.
 *
 * It used to be asked once: "Skip for now" was remembered for good, so anybody
 * who skipped it never saw it again and never got a buzz -- nobody can be
 * pushed a reminder to allow pushes. So a skip now lasts a day, and the screen
 * comes back on the next open after that until notifications are on. The old
 * for-good mark counts as a skip made long ago.
 *
 * Somebody with notifications on is left alone: asked once to install, if they
 * have not, and never again.
 */

/** How long "Skip for now" holds before the screen is offered again. */
export const REASK_MS = 24 * 60 * 60 * 1000;

export type Permission = 'default' | 'denied' | 'granted' | 'unsupported';

export function permissionNow(): Permission {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

/** When "Skip for now" was last tapped, from what was stored: null for never. */
export function skippedAt(stored: string | null): number | null {
  if (stored === null) return null;
  const at = Number(stored);
  // "1" is the old for-good mark: a skip, at the start of time.
  return stored === '1' || !Number.isFinite(at) ? 0 : at;
}

export function shouldAsk({
  installed,
  permission,
  skipped,
  now,
}: {
  installed: boolean;
  permission: Permission;
  skipped: number | null;
  now: number;
}): boolean {
  if (permission === 'granted') return !installed && skipped === null;
  return skipped === null || now - skipped >= REASK_MS;
}
